module Main exposing (Model, Msg(..), init, main, update, view)

import Browser
import Browser.Dom as Dom
import Browser.Events
import Data.Country as Country exposing (Country)
import Helpers.Geometry as Geometry
import Helpers.LargeNumber as LargeNumber exposing (LargeNumber)
import Helpers.Range as Range exposing (Range)
import Helpers.String as String
import Helpers.Timer as Timer exposing (Timer)
import Html as H exposing (Html)
import Html.Attributes as HA
import Html.Events as Ev
import Page exposing (Page(..))
import Page.Quiz as Quiz
import Random
import Random.List
import Task
import Time exposing (Posix)



-- MAIN


main : Program Flags Model Msg
main =
    Browser.element
        { init = init
        , update = update
        , view = view
        , subscriptions = subscriptions
        }



-- MODEL


type alias Flags =
    { countryDtos : List Country.Dto }


type alias Model =
    { appStatus : AppStatus
    , screen : Screen
    , countries : List Country
    , settings : Settings
    }


type AppStatus
    = Loading
    | Running AppStatusRunningData


type alias AppStatusRunningData =
    { dimensions : Geometry.DimensionsRectangle
    , isPaused : Bool
    }


type Screen
    = MainScreen
    | SubPage Page


type alias Settings =
    { continentFilter : ContinentFilter
    , sampleSize : Int
    , populationRange : Range Int
    , hinttimeMs : Float
    }


type ContinentFilter
    = AllContinents
    | ContinentFilter Country.Continent


init : Flags -> ( Model, Cmd Msg )
init { countryDtos } =
    let
        countries =
            List.map Country.dtoToCountry countryDtos

        populationRange =
            defaults.populationRange countries
    in
    ( { appStatus = Loading
      , countries = countries
      , screen = MainScreen
      , settings =
            { continentFilter = AllContinents
            , sampleSize = defaults.sampleSize
            , populationRange = populationRange
            , hinttimeMs = defaults.hinttimeMs
            }
      }
    , Dom.getViewport
        |> Task.map (\{ viewport } -> Tuple.pair (floor viewport.width) (floor viewport.height))
        |> Task.perform GotViewport
    )


countrycodeToImageUri : String -> String
countrycodeToImageUri cc =
    "country-flags/"
        ++ String.toLower cc
        ++ ".svg"


defaults =
    { hinttimeMs = 5000
    , sampleSize = 10
    , populationRange =
        \cs ->
            cs
                |> List.map .population
                |> (\ps ->
                        --Range.init ( List.minimum ps |> Maybe.withDefault 0, List.maximum ps |> Maybe.withDefault 1 ) |> Range.clampRange ( List.minimum ps |> Maybe.withDefault 0, List.maximum ps |> Maybe.withDefault 1 )
                        Range.init ( 0, List.maximum ps |> Maybe.withDefault 0 )
                            |> Range.clampRange ( 0, List.maximum ps |> Maybe.withDefault 0 |> toMillion True |> fromMillion )
                   )
    }



-- TEMP


toMillion : Bool -> Int -> Int
toMillion roundUp =
    toFloat
        >> (\n -> n / 1000000)
        >> (if roundUp then
                ceiling

            else
                floor
           )


fromMillion : Int -> Int
fromMillion =
    (*) 1000000



-- INTEROP


subscriptions : Model -> Sub Msg
subscriptions { screen } =
    let
        pageSubscriptions =
            case screen of
                MainScreen ->
                    []

                SubPage page ->
                    case page of
                        QuizPage _ ->
                            [ Browser.Events.onAnimationFrameDelta Tick ]
    in
    Sub.batch <|
        [ Browser.Events.onVisibilityChange GotVisibility
        , Browser.Events.onResize (\w h -> GotViewport ( w, h ))
        ]
            ++ pageSubscriptions



-- PAGE CONFIG


pageConfig =
    { quizConfig = { eventRelay = QuizEvent, quit = ExitPage } }



-- HELPERS


filterCountries : Settings -> List Country -> List Country
filterCountries { continentFilter, populationRange } countries =
    countries
        |> List.filter (\c -> Range.isWithin c.population populationRange)
        |> List.filter
            (\c ->
                case continentFilter of
                    AllContinents ->
                        True

                    ContinentFilter filter ->
                        Country.isPartOfContinent c filter
            )



-- UPDATE


type Msg
    = NoOp
    | GotViewport ( Int, Int )
    | GotVisibility Browser.Events.Visibility
    | SettingEdited Setting String
    | ExitPage
    | RequestLoadQuiz
    | LoadQuiz (List Country)
    | Tick Float
    | QuizEvent Quiz.Event


type Setting
    = HintTime
    | SampleSize
    | ContinentSelect
    | MinPopulation
    | MaxPopulation


update : Msg -> Model -> ( Model, Cmd Msg )
update msg ({ screen, appStatus, countries, settings } as model) =
    let
        noChange =
            ( model, Cmd.none )
    in
    case msg of
        NoOp ->
            noChange

        GotViewport ( w, h ) ->
            case appStatus of
                Loading ->
                    ( { model | appStatus = Running { dimensions = { w = w, h = h }, isPaused = False } }
                    , Cmd.none
                    )

                Running runningData ->
                    ( { model | appStatus = Running { runningData | dimensions = { w = w, h = h } } }
                    , Cmd.none
                    )

        GotVisibility visibility ->
            noChange

        SettingEdited settingType value ->
            let
                settings_ =
                    case settingType of
                        HintTime ->
                            { settings
                                | hinttimeMs =
                                    value
                                        |> String.toInt
                                        |> Maybe.map (clamp 2 30)
                                        |> Maybe.map2 (*) (Just 1000)
                                        |> Maybe.map toFloat
                                        |> Maybe.withDefault settings.hinttimeMs
                            }

                        SampleSize ->
                            { settings
                                | sampleSize =
                                    value
                                        |> String.toInt
                                        |> Maybe.map (clamp 1 (filterCountries settings countries |> List.length))
                                        |> Maybe.withDefault settings.sampleSize
                            }

                        ContinentSelect ->
                            let
                                maybeContinent =
                                    List.filter
                                        (\c ->
                                            String.compares c.name value
                                        )
                                        Country.continents
                                        |> List.head
                            in
                            { settings
                                | continentFilter = Maybe.map ContinentFilter maybeContinent |> Maybe.withDefault AllContinents
                            }

                        MinPopulation ->
                            { settings
                                | populationRange =
                                    Range.setStart (value |> String.toInt |> Maybe.withDefault 0 |> fromMillion) settings.populationRange
                            }

                        MaxPopulation ->
                            { settings
                                | populationRange =
                                    Range.setEnd (value |> String.toInt |> Maybe.withDefault 0 |> fromMillion) settings.populationRange
                            }
            in
            ( { model | settings = settings_ }
            , Cmd.none
            )

        ExitPage ->
            ( { model | screen = MainScreen }
            , Cmd.none
            )

        RequestLoadQuiz ->
            ( model
            , Random.generate LoadQuiz (Random.List.shuffle countries)
            )

        LoadQuiz shuffledCountries ->
            ( { model
                | screen =
                    Quiz.init
                        (shuffledCountries
                            |> filterCountries settings
                            |> List.take settings.sampleSize
                            |> List.map
                                (\c ->
                                    { name = .name c
                                    , imageUri = countrycodeToImageUri c.code
                                    }
                                )
                        )
                        (Timer.init settings.hinttimeMs)
                        |> QuizPage
                        |> SubPage
              }
            , Task.attempt (\_ -> NoOp) (Dom.focus "guess-input")
            )

        Tick ms ->
            case screen of
                MainScreen ->
                    noChange

                SubPage page ->
                    case page of
                        QuizPage m ->
                            ( { model
                                | screen =
                                    Quiz.tick ms m
                                        |> QuizPage
                                        |> SubPage
                              }
                            , Cmd.none
                            )

        QuizEvent event ->
            case screen of
                MainScreen ->
                    noChange

                SubPage page ->
                    case page of
                        QuizPage m ->
                            ( { model
                                | screen =
                                    Quiz.handleEvent event m
                                        |> QuizPage
                                        |> SubPage
                              }
                            , Cmd.none
                            )



-- VIEW


view : Model -> Html Msg
view ({ appStatus, screen } as model) =
    case appStatus of
        Loading ->
            H.text "loading..."

        Running { dimensions } ->
            case screen of
                MainScreen ->
                    viewMain dimensions model

                SubPage page ->
                    Page.view dimensions pageConfig page


viewSettings : Model -> Html Msg
viewSettings { settings, countries } =
    let
        fontSize =
            20

        input : List (H.Attribute msg) -> List (Html msg) -> Html msg
        input customAttributes =
            H.input <|
                [ HA.style "font-size" (String.px fontSize)
                , HA.style "width" (fontSize * 3 |> String.px)
                , HA.style "text-align" "center"
                ]
                    ++ customAttributes

        select : List (H.Attribute msg) -> List (Html msg) -> Html msg
        select customAttributes =
            H.select <|
                [ HA.style "font-size" (String.px fontSize) ]
                    ++ customAttributes
    in
    H.div
        [ HA.style "display" "flex"
        , HA.style "flex-direction" "column"
        , HA.style "gap" "40px"
        , HA.style "font-size" (String.px fontSize)
        ]
        [ H.div
            []
            [ H.text "You'll be presented with the flag of a random country. Type the country's name. A hint will be given each "
            , input
                [ HA.class "input-setting"
                , HA.type_ "number"
                , HA.style "width" "40px"
                , HA.value (settings.hinttimeMs / 1000 |> String.fromFloat)
                , Ev.onInput (SettingEdited HintTime)
                ]
                []
            , H.text " seconds after not finding a matching letter."
            ]
        , H.div
            [ HA.style "display" "flex_"
            , HA.style "justify-content" "space-between"
            ]
            [ H.text "The quiz may include countries from "
            , select
                [ HA.class "input-setting"
                , HA.style "text-align" "center"
                , Ev.onInput (SettingEdited ContinentSelect)
                ]
                (H.option
                    [ HA.value "all"
                    ]
                    [ H.text "all continents" ]
                    :: List.map
                        (\c ->
                            H.option
                                [ HA.value c.name
                                ]
                                [ H.text c.name ]
                        )
                        Country.continents
                )
            , H.text " where population size is at least "
            , input
                [ HA.class "input-setting"
                , HA.type_ "number"
                , HA.style "text-align" "center"
                , HA.style "width" "65px"
                , HA.value (Range.start settings.populationRange |> toMillion False |> String.fromInt)
                , Ev.onInput (SettingEdited MinPopulation)
                ]
                []
            , H.text " million"
            , H.text " and at most "
            , input
                [ HA.class "input-setting"
                , HA.type_ "number"
                , HA.style "text-align" "center"
                , HA.style "width" "65px"
                , HA.value (Range.end settings.populationRange |> toMillion True |> String.fromInt)
                , Ev.onInput (SettingEdited MaxPopulation)
                ]
                []
            , H.text " million."
            ]
        , H.div
            []
            [ H.text "Pick "
            , input
                [ HA.class "input-setting"
                , HA.style "width" "50px"
                , HA.type_ "number"
                , HA.value (String.fromInt settings.sampleSize)
                , Ev.onInput (SettingEdited SampleSize)
                ]
                []
            , H.text " random flags from "
            , H.text (String.fromInt (List.length (filterCountries settings countries)) ++ " countries.")
            ]
        ]


viewMain : Geometry.DimensionsRectangle -> Model -> Html Msg
viewMain dimensions model =
    H.div
        [ HA.style "display" "flex"
        , HA.style "align-items" "center"
        , HA.style "padding" "0px 20px"
        , HA.style "flex-direction" "column"
        , HA.style "justify-content" "center"
        , HA.style "min-height" "100%"
        , HA.style "box-sizing" "border-box"
        , HA.style "background-color" "#fff8e7"

        --, HA.style "background-color" "#e8eaed"
        ]
        [ H.div
            [ HA.style "max-width" "800px"
            , HA.style "color" "#1d3557"
            ]
            [ H.div
                [ HA.style "margin-bottom" "60px" ]
                [ H.h1
                    [ HA.style "margin-bottom" "0"
                    ]
                    [ H.text "Flag Quiz" ]
                , H.h2
                    [ HA.style "margin-top" "0"
                    ]
                    [ H.text "— guess the country by its flag" ]
                ]
            , H.div
                --[ HA.style "width" "600px"
                [ HA.style "display" "flex"
                , HA.style "flex-direction" "column"
                , HA.style "gap" "60px"
                ]
                [ viewSettings model
                , H.div
                    [ HA.style "display" "flex"
                    , HA.style "align-items" "center"
                    , HA.style "justify-content" "center"
                    ]
                    [ H.button
                        [ HA.style "width" "400px"
                        , HA.style "height" "60px"
                        , HA.style "font-size" "20px"
                        , HA.style "background-color" "#a8dadc"
                        , Ev.onClick RequestLoadQuiz
                        ]
                        [ H.text "Start Quiz" ]
                    ]
                ]
            ]
        ]
